import { AgentSessionStatus, AgentSessionStep, AgentSessionStepType } from "@/modules/sessions/types";
import { updateAgentSessionStep } from "@/modules/sessions/requests";
import { useLoggedInMember } from "@/modules/auth/states";
import { IntakeReviewStepper } from "@/modules/third-party-risk/pages/intake-review/components/IntakeReviewStepper";
import {
  markAssessmentVendorFilled,
  getActiveControlsQuestionnaires,
  setAssessmentControlsQuestionnaires,
  type AvailableControlSet,
  type AvailableQuestionnaire,
} from "@/modules/third-party-risk/requests/intake-form-v2";
import { sendThirdPartyVendorFormEmail } from "@/modules/vendors/requests/index";
import requestConfig from "@config/request";
import { ZButton } from "@/shared/components/button";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import { addNotification } from "@/shared/states/notification";
import { Input, Select, SelectItem, Skeleton, Switch, Tooltip } from "@nextui-org/react";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAgentData, getAgentStateActions, useAgentSessionData } from "../../states";
import { useIntakeFormStore } from "../../states/intake-form";
import { AGENT_TYPES, MultiVendorAssessmentTypes } from "../../types";
import { formatVendorIntakeFormError, processFileForVendorAssessment } from "../../use-cases/vendor.use-case";

interface IdParams {
  id: string;
}

const STEPS = ["Form", "Launch"];

export default function LaunchStep() {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const sessiondata = useAgentSessionData(id);
  const loggedInMember = useLoggedInMember();

  const multiVendor = (sessiondata as any)?.multi_file_vendor_assessment;

  const storeState = useIntakeFormStore.getState();
  const [sendToVendor, setSendToVendor] = useState(storeState.launchSendToVendor);
  const [contactName, setContactName] = useState(storeState.launchContactName);
  const [contactEmail, setContactEmail] = useState(storeState.launchContactEmail);
  const [selectedControlSetIds, setSelectedControlSetIds] = useState<string[]>(
    storeState.launchControlSetIds,
  );
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string>(
    storeState.launchQuestionnaireId,
  );
  const [availableControlSets, setAvailableControlSets] = useState<AvailableControlSet[]>([]);
  const [availableQuestionnaires, setAvailableQuestionnaires] = useState<AvailableQuestionnaire[]>(
    [],
  );
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    controlSets?: string;
    questionnaire?: string;
  }>({});
  const isSubmittingRef = useRef(false);

  const { isContinueDisabled, continueTooltip } = useMemo(() => {
    if (isLoadingOptions) {
      return { isContinueDisabled: true, continueTooltip: "Loading available options..." };
    }
    if (availableControlSets.length === 0) {
      return { isContinueDisabled: true, continueTooltip: "No control sets available" };
    }
    if (selectedControlSetIds.length === 0) {
      return { isContinueDisabled: true, continueTooltip: "Please select at least one control set" };
    }
    if (sendToVendor && !selectedQuestionnaireId) {
      return { isContinueDisabled: true, continueTooltip: "Please select a questionnaire" };
    }
    if (isSubmitting) {
      return { isContinueDisabled: true, continueTooltip: "" };
    }
    return { isContinueDisabled: false, continueTooltip: "" };
  }, [isLoadingOptions, availableControlSets.length, selectedControlSetIds.length, sendToVendor, selectedQuestionnaireId, isSubmitting]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getActiveControlsQuestionnaires();
        setAvailableControlSets(data.available_control_sets);
        setAvailableQuestionnaires(data.available_questionnaires);
      } catch (error) {
        console.error("Failed to load controls/questionnaires:", error);
        addNotification({
          type: "error",
          message: "Failed to load available options. Please try again.",
        });
      } finally {
        setIsLoadingOptions(false);
      }
    };
    void load();
  }, []);

  const handleBack = () => {
    setSearchParams(prev => { prev.set("reviewStep", "1"); return prev; });
  };

  const validateFields = (): boolean => {
    const newErrors: {
      name?: string;
      email?: string;
      controlSets?: string;
      questionnaire?: string;
    } = {};

    if (selectedControlSetIds.length === 0) {
      newErrors.controlSets = "Please select at least one control set";
    }

    if (sendToVendor) {
      if (!contactName.trim()) {
        newErrors.name = "Contact name is required";
      }
      if (!contactEmail.trim()) {
        newErrors.email = "Contact email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!selectedQuestionnaireId) {
        newErrors.questionnaire = "Please select a questionnaire";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (isSubmittingRef.current) return;
    if (!validateFields()) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (sendToVendor) {
        // 1. Set control sets and questionnaire
        await setAssessmentControlsQuestionnaires(id, {
          control_set_ids: selectedControlSetIds,
          questionnaire_id: selectedQuestionnaireId,
        });

        // 2. Save contact info to backend
        await markAssessmentVendorFilled(id, {
          vendor_email_address: contactEmail,
          contact_email_address: loggedInMember?.email_address ?? "",
          contact_name: contactName,
        });

        // 3. Send the email to vendor
        await sendThirdPartyVendorFormEmail({
          agent_session_id: id,
          vendor_email: contactEmail,
          contact_email: loggedInMember?.email_address ?? "",
          vendor_name: multiVendor?.vendor_name ?? "",
          ui_redirect_url: `${requestConfig.exchangeBaseUrl}/zania-exchange-hub`,
          request_type: "initial_request",
        });

        // 4. Update local state (replicate processFileForVendorAssessment email path)
        const agentData = getAgentData<
          AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
          MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
        >(id);

        if (agentData) {
          const updatedStepData = agentData.stepData.map((step, index) => {
            if (index === 0 && step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
              return { ...step, status: AgentSessionStatus.COMPLETE };
            }
            return step;
          });

          const { updateAgentSessionData, updateAgentStepData } = getAgentStateActions();
          updateAgentSessionData(id, {
            status: AgentSessionStatus.FORM_SENT,
            multi_file_vendor_assessment: {
              ...multiVendor,
              vendor_email: contactEmail,
              contact_email: loggedInMember?.email_address ?? "",
              vendor_name: multiVendor?.vendor_name ?? "",
            },
          });
          updateAgentStepData(id, updatedStepData);

          const profileStep = updatedStepData.find(
            (step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE
          );
          if (profileStep) {
            updateAgentSessionStep(profileStep as AgentSessionStep);
          }
        }

        // 5. Navigate to agent page (shows FORM_SENT state)
        useIntakeFormStore.getState().resetLaunchStep();
        navigate({ pathname: `/agent/${id}/` });
      } else {
        // Toggle OFF — set control sets (no questionnaire), then normal RAG processing flow
        await setAssessmentControlsQuestionnaires(id, {
          control_set_ids: selectedControlSetIds,
        });
        useIntakeFormStore.getState().resetLaunchStep();
        await processFileForVendorAssessment(id, navigate);
      }
    } catch (error) {
      console.error("Failed to launch assessment:", error);
      const errorMessage = formatVendorIntakeFormError(error);
      addNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full bg-[#FAFAFA] bg-opacity-90 flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-[#E2E8F0] w-full bg-white">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <VendorLogo
            alt={multiVendor?.vendor_name || ""}
            src={multiVendor?.vendor_logo_url || ""}
            needsAuthToken
            className="border border-[#E2E8F0]"
          />
          <span className="font-semibold text-2xl leading-7 text-[#020618] truncate">
            {multiVendor?.vendor_name}{" "}
            {multiVendor?.product_name || ""}
          </span>
        </div>
        <IntakeReviewStepper currentStep={2} steps={STEPS} />
        <div className="flex-1" />
      </div>

      {/* Content */}
      <div className="flex-1 w-full overflow-y-auto p-6">
        <div className="flex flex-col gap-6 p-6 bg-white rounded-lg shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)]">
          {/* Title section */}
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold leading-6 text-[#0F172B]">
              Configure Information Gathering
            </h2>
            <p className="text-sm font-normal leading-5 text-[#62748E]">
              Choose how you'd like to collect the remaining information for this assessment.
            </p>
          </div>

          {/* Control Sets Card */}
          <div className="flex flex-col gap-4 p-6 border border-[#E2E8F0] rounded-lg">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-[#0F172B]">Control Sets</p>
              <p className="text-xs text-[#62748E]">Select the control sets to include in this assessment.</p>
            </div>

            {isLoadingOptions ? (
              <Skeleton className="h-10 rounded-md" />
            ) : (
              <Select
                label="Control Sets"
                placeholder="Select control sets"
                selectionMode="multiple"
                selectedKeys={new Set(selectedControlSetIds)}
                onSelectionChange={(keys) => {
                  const ids = keys === "all"
                    ? availableControlSets.map((cs) => cs.id)
                    : Array.from(keys as Set<string>);
                  setSelectedControlSetIds(ids);
                  useIntakeFormStore.getState().setLaunchControlSetIds(ids);
                  if (errors.controlSets) setErrors((prev) => ({ ...prev, controlSets: undefined }));
                }}
                isRequired
                isInvalid={!!errors.controlSets}
                errorMessage={errors.controlSets}
                variant="bordered"
                size="sm"
                classNames={{
                  trigger: "border-[#E2E8F0]",
                }}
                renderValue={(items) => {
                  if (!items || items.length === 0) return null;
                  if (items.length === 1) return <span>{items[0].textValue}</span>;
                  return (
                    <span>
                      {items[0].textValue}{" "}
                      <span className="text-[#006FEE]">+{items.length - 1} more</span>
                    </span>
                  );
                }}
              >
                {availableControlSets.map((cs) => (
                  <SelectItem key={cs.id} value={cs.id} textValue={`${cs.controls_set_name} (${cs.number_of_controls} controls)`}>
                    {`${cs.controls_set_name} (${cs.number_of_controls} controls)`}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>

          {/* Toggle Card */}
          <div className="flex flex-col gap-6 p-6 border border-[#E2E8F0] rounded-lg">
            <div className="flex items-center gap-3">
              <Switch
                isSelected={sendToVendor}
                onValueChange={(val) => {
                  setSendToVendor(val);
                  useIntakeFormStore.getState().setLaunchSendToVendor(val);
                  if (!val) {
                    setErrors((prev) => ({ ...prev, questionnaire: undefined }));
                  }
                }}
                size="sm"
                color="primary"
              />
              <div className="flex flex-col gap-1 flex-1">
                <p className="text-sm font-semibold leading-5 text-[#0F172B]">
                  Request third-party to complete
                </p>
                <p className="text-xs leading-4 text-[#62748E]">
                  Send the questionnaire and document request to the third-party for their input.
                </p>
              </div>
            </div>

            {/* Contact fields shown when toggle is ON */}
            {sendToVendor && (
              <div className="flex flex-col gap-3 border-l border-[#006FEE] pl-6">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      label="Contact Name"
                      placeholder="Enter contact name"
                      value={contactName}
                      onValueChange={(val) => {
                        setContactName(val);
                        useIntakeFormStore.getState().setLaunchContactName(val);
                        if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      isRequired
                      isInvalid={!!errors.name}
                      errorMessage={errors.name}
                      variant="bordered"
                      size="sm"
                      classNames={{
                        inputWrapper: "border-[#E2E8F0]",
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      label="Contact Email"
                      placeholder="Enter contact email"
                      type="email"
                      value={contactEmail}
                      onValueChange={(val) => {
                        setContactEmail(val);
                        useIntakeFormStore.getState().setLaunchContactEmail(val);
                        if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      isRequired
                      isInvalid={!!errors.email}
                      errorMessage={errors.email}
                      variant="bordered"
                      size="sm"
                      classNames={{
                        inputWrapper: "border-[#E2E8F0]",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Questionnaire Card — only when sending to vendor */}
          {sendToVendor && (
            <div className="flex flex-col gap-4 p-6 border border-[#E2E8F0] rounded-lg">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-[#0F172B]">Questionnaire</p>
                <p className="text-xs text-[#62748E]">Select the questionnaire to send to the vendor.</p>
              </div>

              {isLoadingOptions ? (
                <Skeleton className="h-10 rounded-md" />
              ) : (
                <Select
                  label="Questionnaire"
                  placeholder="Select a questionnaire"
                  selectedKeys={selectedQuestionnaireId ? new Set([selectedQuestionnaireId]) : new Set()}
                  onSelectionChange={(keys) => {
                    const id = keys === "all" ? "" : Array.from(keys as Set<string>)[0] ?? "";
                    setSelectedQuestionnaireId(id);
                    useIntakeFormStore.getState().setLaunchQuestionnaireId(id);
                    if (errors.questionnaire) setErrors((prev) => ({ ...prev, questionnaire: undefined }));
                  }}
                  isRequired
                  isInvalid={!!errors.questionnaire}
                  errorMessage={errors.questionnaire}
                  variant="bordered"
                  size="sm"
                  classNames={{
                    trigger: "border-[#E2E8F0]",
                  }}
                >
                  {availableQuestionnaires.map((q) => (
                    <SelectItem
                      key={q.id}
                      value={q.id}
                      isDisabled={q.number_of_questions === 0 && q.number_of_evidences === 0}
                    >
                      {`${q.questionnaire_name} (${q.number_of_questions} questions)`}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          )}

          {/* Buttons inside the card */}
          <div className="flex justify-between items-center gap-6">
            <ZButton
              variant="bordered"
              onClick={handleBack}
              isDisabled={isSubmitting}
              className="h-10 px-4 py-2 border-[#006FEE] rounded-md text-base font-medium text-[#006FEE]"
            >
              Back
            </ZButton>
            <Tooltip
              content={continueTooltip}
              isDisabled={!isContinueDisabled || !continueTooltip}
              placement="top"
              showArrow
              classNames={{
                content: "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny max-w-[300px]",
              }}
            >
              <div>
                <ZButton
                  onClick={() => void handleContinue()}
                  isDisabled={isContinueDisabled}
                  className="h-10 px-4 py-2 bg-[#006FEE] rounded-md text-base font-medium text-white"
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Continue
                </ZButton>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
